import { type ReactNode, useEffect, useMemo, useState } from 'react';
import Button from '@rio-cloud/rio-uikit/Button';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import Table, {
    TableBody,
    TableColumn,
    TableExpandedRow,
    TableExpanderButton,
    TableHeader,
    TableHeaderColumn,
    TableRow,
} from '@rio-cloud/rio-uikit/Table';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import {
    getAssignedAssets,
    getAssignedAssetsLoaded,
    getAssignedDrivers,
    getAssignedDriversLoaded,
    getSelectedTag,
    getTags,
    getTagsFetchStatus,
} from '../Tags.selector';
import { fetchStatus } from '../tagsSlice';
import { fetchAllTags } from '../tagsThunks';
import { getUserAccount } from '../../../../configuration/login/loginSlice';
import { openTagCreationDialog } from '../create/tagCreationDialogSlice';
import { TagCreationDialog } from '../create/TagCreationDialog';
import { buildHierarchy, searchHierarchy, type HierarchyNode } from './hierarchy';
import { closableSuccessNotification, nonVanishingErrorNotification } from '../../ClickableNotifications';

export const TagHierarchy = () => {
    const tags = useAppSelector(getTags);
    const account = useAppSelector(getUserAccount);
    const complete = useAppSelector(getTagsFetchStatus) === fetchStatus.SUCCEEDED;
    const selected = useAppSelector(getSelectedTag);
    const assets = useAppSelector(getAssignedAssets);
    const drivers = useAppSelector(getAssignedDrivers);
    const assetsLoaded = useAppSelector(getAssignedAssetsLoaded);
    const driversLoaded = useAppSelector(getAssignedDriversLoaded);
    const deletions = useAppSelector(state => state.tags.deletions);
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search).get('q') ?? '';
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const tree = useMemo(() => searchHierarchy(buildHierarchy(tags), query), [tags, query]);

    useEffect(() => {
        if (deletions.lastSuccess) {
            closableSuccessNotification(
                <FormattedMessage id='tags.tagList.delete.success' values={{ tagName: deletions.lastSuccess }} />,
                'success-notification-tag-deleted'
            );
        }
        if (deletions.lastFailure) {
            nonVanishingErrorNotification(
                <FormattedMessage id='tags.tagList.delete.failure' values={{ tagName: deletions.lastFailure }} />,
                'T01'
            );
        }
    }, [deletions.lastSuccess, deletions.lastFailure]);

    // Expanded child rows are siblings in the table body. Depth is represented in
    // the name cell so every level keeps the same column layout.
    const renderNodes = (nodes: HierarchyNode[], depth = 0, parentKey?: string): ReactNode[] =>
        nodes.flatMap(node => {
            const open = !!query.trim() || expanded.has(node.key);
            const parentId = node.tags[0]?.id ?? `virtual:${node.path}`;
            const rows = (node.tags.length ? node.tags : [undefined]).map((tag, index) => {
                const id = tag?.id ?? parentId;
                const content = (
                    <>
                        <TableColumn columnKey='name'>
                            <div className='display-flex align-items-center gap-5' style={{ paddingLeft: depth * 20 }}>
                                {node.children.length > 0 && index === 0 ? (
                                    <TableExpanderButton
                                        aria-label={`${open ? 'Collapse' : 'Expand'} ${node.path}`}
                                        aria-expanded={open}
                                        expanded={open}
                                        disabled={!!query.trim()}
                                        onClick={event => {
                                            event.stopPropagation();
                                            setExpanded(previous => {
                                                const next = new Set(previous);
                                                if (next.has(node.key)) {
                                                    next.delete(node.key);
                                                } else {
                                                    next.add(node.key);
                                                }
                                                return next;
                                            });
                                        }}
                                    />
                                ) : (
                                    <span className='width-20 flex-shrink-0' aria-hidden='true' />
                                )}
                                {tag ? (
                                    <Button
                                        variant={Button.VARIANT_LINK}
                                        aria-current={selected?.id === tag.id ? 'page' : undefined}
                                        title={tag.name}
                                        onClick={event => {
                                            event.stopPropagation();
                                            navigate({ pathname: `/tags/${tag.id}`, search: location.search });
                                        }}
                                    >
                                        {node.label}
                                    </Button>
                                ) : (
                                    <span title={node.path}>
                                        {node.label} <small>(virtual grouping)</small>
                                    </span>
                                )}
                                {node.irregular && <small>Irregular path — shown unchanged</small>}
                            </div>
                        </TableColumn>
                        <TableColumn columnKey='path' overflow='ellipsis'>
                            <span title={node.path} style={{ whiteSpace: 'pre-wrap' }}>
                                {node.path}
                            </span>
                            {node.tags.length > 1 && <small> ID: {tag?.id}</small>}
                        </TableColumn>
                        <TableColumn columnKey='assets'>
                            {tag ? (assetsLoaded ? (assets[tag.id]?.length ?? 0) : '…') : '—'}
                        </TableColumn>
                        <TableColumn columnKey='drivers'>
                            {tag ? (driversLoaded ? (drivers[tag.id]?.length ?? 0) : '…') : '—'}
                        </TableColumn>
                    </>
                );
                const rowProps = {
                    id,
                    tagId: tag?.id,
                    selectable: false,
                    noHover: !tag,
                    'data-cy': tag ? 'hierarchy-tag-row' : 'hierarchy-virtual-row',
                };
                return parentKey === undefined ? (
                    <TableRow key={id} {...rowProps}>
                        {content}
                    </TableRow>
                ) : (
                    <TableExpandedRow key={id} parentKey={parentKey} {...rowProps}>
                        {content}
                    </TableExpandedRow>
                );
            });
            return [...rows, ...(open ? renderNodes(node.children, depth + 1, parentId) : [])];
        });

    return (
        <div className='padding-15' data-cy='tag-hierarchy'>
            <div className='display-flex gap-10 margin-bottom-15'>
                <Button bsStyle='primary' disabled={!complete} onClick={() => dispatch(openTagCreationDialog())}>
                    Create root
                </Button>
                <div className='min-width-200 flex-1-1' style={{ maxWidth: 480 }}>
                    <TableSearch
                        value={query}
                        placeholder='Search full paths'
                        onChange={value => {
                            const params = new URLSearchParams(location.search);
                            if (value) {
                                params.set('q', value);
                            } else {
                                params.delete('q');
                            }
                            navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
                        }}
                    />
                </div>
            </div>
            {!complete ? (
                <div role='alert'>
                    All groups could not be loaded. Creation and deletion are unavailable.
                    <Button onClick={() => dispatch(fetchAllTags({ accountId: account ?? null }))}>Retry</Button>
                </div>
            ) : (
                <>
                    <p>
                        Paths organize fleet groups. Role restrictions and resource assignments are not inherited.
                        Renaming and moving groups are unavailable.
                    </p>
                    {tree.length ? (
                        <Table
                            rowKey='id'
                            activeRowId={selected?.id}
                            hover
                            expandedRowAnimation={false}
                            stickyReference='applicationLayout'
                            onRowClick={row => {
                                if (typeof row.tagId === 'string') {
                                    navigate({ pathname: `/tags/${row.tagId}`, search: location.search });
                                }
                            }}
                        >
                            <TableHeader sticky>
                                <TableHeaderColumn columnKey='name' width='40%'>
                                    Group name
                                </TableHeaderColumn>
                                <TableHeaderColumn columnKey='path' width='30%'>
                                    Full path
                                </TableHeaderColumn>
                                <TableHeaderColumn columnKey='assets' width='15%'>
                                    Direct assets
                                </TableHeaderColumn>
                                <TableHeaderColumn columnKey='drivers' width='15%'>
                                    Direct drivers
                                </TableHeaderColumn>
                            </TableHeader>
                            <TableBody aria-label='fleet group hierarchy'>{renderNodes(tree)}</TableBody>
                        </Table>
                    ) : (
                        <p>{tags.length ? 'No matching groups.' : 'No groups yet. Create a root group to begin.'}</p>
                    )}
                </>
            )}
            <TagCreationDialog />
        </div>
    );
};
